import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { IconArrowUpRight, IconClock, IconExternalLink, IconMapPin, IconVideo } from "@tabler/icons-react"
import type { IChapterContent, ILocation } from "@dc-ireland/shared"
import apiClient from "../../../lib/api"
import { getChapterDefaults } from "../../../lib/chapterContent"
import {
  buildDirectionsUrl,
  getChapterMeta,
  getChapterSocialIcons,
  isPhysicalAddress,
  parseChapterServiceTimes,
} from "../../../lib/chapters"
import SocialIcons from "../../SocialIcons"
import { resolveImageUrl } from "../../../lib/imageUtils"
import LandingNav from "../LandingNav"
import LandingFooter from "../LandingFooter"
import ChapterGallerySection from "./ChapterGallerySection"
import ChapterCellGroupsSection from "./ChapterCellGroupsSection"
import ChapterMinistriesSection from "./ChapterMinistriesSection"
// import ChapterServicesSection from "./ChapterServicesSection"
// import ChapterNewsSection from "./ChapterNewsSection"
// import ChapterBirthdaysSection from "./ChapterBirthdaysSection"
// import ChapterEventsSection from "./ChapterEventsSection"
import ChapterSectionNav from "./ChapterSectionNav"

type Props = {
  slug: string
}

const emptyChapterContent: IChapterContent = {
  gallery: [],
  cellGroups: [],
  birthdays: [],
}

const baseSectionLinks = [
  // { href: "#events", label: "Events" },
  { href: "#cell-groups", label: "Cell Groups" },
  { href: "#ministries", label: "Ministries" },
  // { href: "#services", label: "Services" },
  // { href: "#news", label: "News" },
  // { href: "#birthdays", label: "Birthdays" },
  { href: "#gallery", label: "Gallery" },
]

const ChapterPage: React.FC<Props> = ({ slug }) => {
  const meta = getChapterMeta(slug)
  const defaults = getChapterDefaults(slug)
  const prefersReducedMotion = useReducedMotion()
  const [location, setLocation] = useState<ILocation | null>(null)
  const [chapterContent, setChapterContent] = useState<IChapterContent>(emptyChapterContent)
  const [contentLoading, setContentLoading] = useState(true)

  useEffect(() => {
    apiClient
      .getLocations()
      .then((locations) => setLocation(locations.find((l) => l.slug === slug) ?? null))
      .catch(() => setLocation(null))
  }, [slug])

  useEffect(() => {
    setContentLoading(true)
    apiClient
      .getChapterContent(slug)
      .then(setChapterContent)
      .catch(() => setChapterContent(emptyChapterContent))
      .finally(() => setContentLoading(false))
  }, [slug])

  if (!meta || !defaults) return null

  const socialIcons = getChapterSocialIcons(meta.socials)
  const hasMinistries = defaults.ministries.length > 0
  const physicalAddress = Boolean(location && isPhysicalAddress(location.address))
  const directionsUrl = physicalAddress && location ? buildDirectionsUrl(location.address) : undefined
  const addressLines = physicalAddress && location ? location.address : []
  const { sundayTime, midweekTime } = parseChapterServiceTimes(location?.times ?? [])
  const bannerServices = [
    {
      key: "sunday",
      title: defaults.sundayService.title ?? "Sunday Service",
      time: sundayTime,
      addressLines,
      directionsUrl,
      zoomUrl: defaults.sundayService.zoomUrl,
    },
    ...(defaults.midweekService
      ? [
          {
            key: "midweek",
            title: defaults.midweekService.title ?? "Midweek Service",
            time: midweekTime,
            addressLines: defaults.midweekService.showAddress ? addressLines : [],
            directionsUrl: defaults.midweekService.showAddress ? directionsUrl : undefined,
            zoomUrl: defaults.midweekService.zoomUrl,
          },
        ]
      : []),
  ]
  const bannerLinkClass =
    "inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3.5 py-1.5 font-dm-sans text-sm font-semibold leading-none text-white transition hover:border-white/40 hover:bg-white/5"
  const sectionLinks = hasMinistries
    ? baseSectionLinks
    : baseSectionLinks.filter((link) => link.href !== "#ministries")

  return (
    <div className="bg-[#fafafa]">
      <LandingNav />

      {/* Hero */}
      <section className="relative flex min-h-[55vh] items-end overflow-hidden bg-[#0a0a12] pb-14 pt-24 md:min-h-[60vh] md:pb-20 md:pt-28">
        <img
          src={resolveImageUrl(meta.heroImage)}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a12] via-[#0a0a12]/60 to-[#0a0a12]/30" />

        <div className="relative mx-auto flex w-full max-w-6xl flex-col px-4 md:px-8 lg:px-20">
          <motion.div
            initial={prefersReducedMotion ? undefined : { opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="font-dm-sans text-xs font-semibold uppercase tracking-[0.28em] text-[#9e7e43]">
              Dominion city {meta.name}
            </p>
            <h1 className="mt-3 font-outfit text-5xl font-semibold tracking-tight text-white md:text-6xl lg:text-7xl">
              {meta.name}
            </h1>
            <p className="mt-4 max-w-xl font-dm-sans text-base leading-7 text-white/70 md:text-lg">
              {meta.tagline}
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              {bannerServices.map((service) => (
                <div
                  key={service.key}
                  className="flex min-w-[240px] flex-col gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-sm"
                >
                  <p className="font-outfit text-base font-semibold text-white md:text-lg">{service.title}</p>
                  {service.addressLines.length > 0 && (
                    <div className="flex items-start gap-2">
                      <span className="flex h-5 shrink-0 items-center">
                        <IconMapPin size={16} className="text-[#9e7e43]" />
                      </span>
                      <div className="font-dm-sans text-sm leading-5 text-white/80">
                        {service.addressLines.map((line) => (
                          <p key={line}>{line}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  {service.time && (
                    <div className="flex items-center gap-2">
                      <IconClock size={16} className="shrink-0 text-[#9e7e43]" />
                      <p className="font-dm-sans text-sm leading-5 text-white/80">{service.time}</p>
                    </div>
                  )}
                  {(service.directionsUrl || service.zoomUrl) && (
                    <div className="flex flex-wrap gap-2">
                      {service.directionsUrl && (
                        <a
                          href={service.directionsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={bannerLinkClass}
                        >
                          Get directions
                          <IconArrowUpRight size={14} className="shrink-0 opacity-80" />
                        </a>
                      )}
                      {service.zoomUrl && (
                        <a
                          href={service.zoomUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={bannerLinkClass}
                        >
                          <IconVideo size={14} className="shrink-0" />
                          Join on Zoom
                          <IconExternalLink size={14} className="shrink-0 opacity-70" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {socialIcons.length > 0 && (
              <div className="mt-6">
                <SocialIcons
                  icons={socialIcons}
                  size={18}
                  showLabels
                  goldOnHover
                  className="text-white/85"
                />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      <ChapterSectionNav links={sectionLinks} />

      {/* <ChapterEventsSection chapterSlug={slug} chapterName={meta.name} /> */}

      <ChapterCellGroupsSection
        chapterName={meta.name}
        cellGroups={chapterContent.cellGroups}
        loading={contentLoading}
      />
      {hasMinistries && <ChapterMinistriesSection ministries={defaults.ministries} />}
      {/* <ChapterServicesSection
        location={location}
        sundayPoster={defaults.sundayService}
        midweekPoster={defaults.midweekService}
      /> */}
      {/* <ChapterNewsSection chapterSlug={slug} chapterName={meta.name} /> */}
      {/* <ChapterBirthdaysSection birthdays={chapterContent.birthdays} loading={contentLoading} /> */}

      <ChapterGallerySection images={chapterContent.gallery} loading={contentLoading} />

      {/* CTA */}
      <section className="bg-[#0d0d0d] py-16 md:py-20">
        <div className="mx-auto max-w-2xl px-4 text-center md:px-8">
          <h2 className="font-outfit text-3xl font-semibold text-white md:text-4xl">
            Join us in {meta.name}
          </h2>
          <p className="mt-4 font-dm-sans text-base leading-7 text-white/60">
            Whether you&apos;re new to faith or looking for a church home, you&apos;re welcome here.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="#cell-groups"
              className="rounded-full bg-[#9e7e43] px-6 py-3 font-dm-sans text-sm font-semibold text-white transition hover:bg-[#b8954f]"
            >
              Find a Cell Group
            </a>
            <Link
              to="/"
              className="rounded-full border border-white/20 px-6 py-3 font-dm-sans text-sm font-semibold text-white transition hover:border-white/40 hover:bg-white/5"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  )
}

export default ChapterPage
